import {
  type AnyPgColumn,
  boolean,
  date,
  index,
  integer,
  numeric,
  pgTable,
  primaryKey,
  text,
  timestamp,
  uuid,
  varchar,
} from "drizzle-orm/pg-core";
import type { ShopCurrency } from "@/shared/lib/currency";
import { shops } from "./shops";

// =====================================================
// Catalog (Parts + Fitment) — Single-tenant
// =====================================================

export const brands = pgTable(
  "brands",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    name: varchar("name", { length: 255 }).notNull(),
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
  },
  (t) => [index("brands_name").on(t.name)],
);

export const categories = pgTable(
  "categories",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    parentId: uuid("parent_id"),
    name: varchar("name", { length: 255 }).notNull(),
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
  },
  (t) => [index("categories_parent_name").on(t.parentId, t.name)],
);

export const vehicleMakes = pgTable("vehicle_makes", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: varchar("name", { length: 255 }).notNull().unique(),
});

export const vehicleModels = pgTable(
  "vehicle_models",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    makeId: uuid("make_id")
      .notNull()
      .references(() => vehicleMakes.id),
    name: varchar("name", { length: 255 }).notNull(),
  },
  (t) => [index("vehicle_models_make_name").on(t.makeId, t.name)],
);

/** One selectable vehicle spec — replaces vehicle_trims + vehicle_variants. */
export const vehicleConfigurations = pgTable(
  "vehicle_configurations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    modelId: uuid("model_id")
      .notNull()
      .references(() => vehicleModels.id),
    /** OEM platform / chassis family code (e.g. NCP150, FC1, TFR87) */
    modelCode: varchar("model_code", { length: 30 }),
    yearFrom: integer("year_from"),
    yearTo: integer("year_to"),
    /** เดือนเริ่ม (1–12) ใช้คู่กับ year_from */
    monthFrom: integer("month_from"),
    /** เดือนสิ้นสุด (1–12) ใช้คู่กับ year_to */
    monthTo: integer("month_to"),
    engine: varchar("engine", { length: 255 }),
    marketCode: varchar("market_code", { length: 20 }),
    transmissionCode: varchar("transmission_code", { length: 50 }),
    engineCode: varchar("engine_code", { length: 50 }),
    gradeCode: varchar("grade_code", { length: 50 }),
    adminNotes: text("admin_notes"),
    sortOrder: integer("sort_order").default(0).notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull(),
  },
  (t) => [
    index("vehicle_configurations_model_years").on(
      t.modelId,
      t.yearFrom,
      t.yearTo,
    ),
    index("vehicle_configurations_model_code").on(t.modelId, t.modelCode),
  ],
);

/** Vehicle specification & option codes (shared across configurations of the same model). */
export const vehicleModelCodes = pgTable(
  "vehicle_model_codes",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    modelId: uuid("model_id")
      .notNull()
      .references(() => vehicleModels.id, { onDelete: "cascade" }),
    codeType: varchar("code_type", { length: 20 })
      .$type<"spec" | "option">()
      .notNull(),
    code: varchar("code", { length: 50 }).notNull(),
    label: varchar("label", { length: 255 }),
    notes: text("notes"),
    sortOrder: integer("sort_order").default(0).notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull(),
  },
  (t) => [
    index("vehicle_model_codes_model").on(t.modelId),
    index("vehicle_model_codes_unique").on(t.modelId, t.codeType, t.code),
  ],
);

/** Links model-level codes to individual configurations (subset selection). */
export const vehicleConfigurationCodeLinks = pgTable(
  "vehicle_configuration_code_links",
  {
    vehicleConfigurationId: uuid("vehicle_configuration_id")
      .notNull()
      .references(() => vehicleConfigurations.id, { onDelete: "cascade" }),
    vehicleModelCodeId: uuid("vehicle_model_code_id")
      .notNull()
      .references(() => vehicleModelCodes.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull(),
  },
  (t) => [
    primaryKey({
      columns: [t.vehicleConfigurationId, t.vehicleModelCodeId],
    }),
    index("vehicle_configuration_code_links_config").on(
      t.vehicleConfigurationId,
    ),
    index("vehicle_configuration_code_links_code").on(t.vehicleModelCodeId),
  ],
);

/** VIN / chassis prefix หรือช่วง production → map เข้ากับ configuration */
export const vehicleChassisRanges = pgTable(
  "vehicle_chassis_ranges",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    vehicleConfigurationId: uuid("vehicle_configuration_id")
      .notNull()
      .references(() => vehicleConfigurations.id, { onDelete: "cascade" }),
    vinPrefix: varchar("vin_prefix", { length: 17 }),
    chassisPrefix: varchar("chassis_prefix", { length: 60 }),
    chassisFrom: varchar("chassis_from", { length: 60 }),
    chassisTo: varchar("chassis_to", { length: 60 }),
    productionFrom: date("production_from"),
    productionTo: date("production_to"),
    notes: varchar("notes", { length: 500 }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull(),
  },
  (t) => [
    index("vehicle_chassis_ranges_configuration").on(t.vehicleConfigurationId),
    index("vehicle_chassis_ranges_vin_prefix").on(t.vinPrefix),
  ],
);

// =====================================================
// Master Catalog (Admin) — ไม่มี shop_id
// =====================================================

/** สินค้าหลักของแพลตฟอร์ม — จัดการที่ Admin */
export const catalogProducts = pgTable(
  "catalog_products",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    categoryId: uuid("category_id").references(() => categories.id),
    brandId: uuid("brand_id").references(() => brands.id),
    type: varchar("type", { length: 50 })
      .$type<"part_new" | "part_oem" | "part_used" | "service">()
      .notNull(),
    name: varchar("name", { length: 255 }).notNull(),
    description: text("description"),
    status: varchar("status", { length: 50 })
      .$type<"draft" | "active" | "hidden" | "archived" | "candidate">()
      .default("draft")
      .notNull(),
    /** ร้านที่เสนอ master ใหม่ (status=candidate) */
    proposedByShopId: uuid("proposed_by_shop_id").references(() => shops.id),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull(),
  },
  (t) => [
    index("catalog_products_status").on(t.status),
    index("catalog_products_category").on(t.categoryId),
    index("catalog_products_brand").on(t.brandId),
  ],
);

/** SKU หลักต่อ catalog product — ไม่เก็บ price/barcode (เป็นต่อร้านใน shop_product_skus) */
export const catalogSkus = pgTable(
  "catalog_skus",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    catalogProductId: uuid("catalog_product_id")
      .notNull()
      .references(() => catalogProducts.id, { onDelete: "cascade" }),
    oemPartNo: varchar("oem_part_no", { length: 100 }),
    condition: varchar("condition", { length: 50 }).$type<
      "new" | "used" | "refurb"
    >(),
    fitmentStatus: varchar("fitment_status", { length: 50 })
      .$type<"pending" | "complete">()
      .default("complete")
      .notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull(),
  },
  (t) => [
    index("catalog_skus_product").on(t.catalogProductId),
    index("catalog_skus_oem").on(t.oemPartNo),
    index("catalog_skus_fitment_status").on(t.fitmentStatus),
  ],
);

/** สาย supersession พาร์ทเก่า → พาร์ทใหม่ — ควบคุมความเป็นกราฟใน app layer */
export const catalogSkuSupersessions = pgTable(
  "catalog_sku_supersessions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    fromPartNo: varchar("from_part_no", { length: 100 }).notNull(),
    toPartNo: varchar("to_part_no", { length: 100 }).notNull(),
    toCatalogSkuId: uuid("to_catalog_sku_id").references(() => catalogSkus.id, {
      onDelete: "set null",
    }),
    supersededAt: date("superseded_at"),
    notes: text("notes"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull(),
  },
  (t) => [
    index("catalog_sku_supersessions_from").on(t.fromPartNo),
    index("catalog_sku_supersessions_to_part").on(t.toPartNo),
    index("catalog_sku_supersessions_to_sku").on(t.toCatalogSkuId),
    index("catalog_sku_supersessions_from_to").on(t.fromPartNo, t.toPartNo),
  ],
);

/** เลข alternate / OEM-equivalent ต่อ catalog SKU */
export const catalogSkuAlternates = pgTable(
  "catalog_sku_alternates",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    catalogSkuId: uuid("catalog_sku_id")
      .notNull()
      .references(() => catalogSkus.id, { onDelete: "cascade" }),
    altPartNo: varchar("alt_part_no", { length: 100 }).notNull(),
    altBrand: varchar("alt_brand", { length: 100 }),
    altType: varchar("alt_type", { length: 50 }).$type<
      "aftermarket" | "oem_equivalent" | "genuine_other"
    >(),
    notes: text("notes"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull(),
  },
  (t) => [index("catalog_sku_alternates_sku").on(t.catalogSkuId)],
);

/** เช่น torque ทำเป็น key/value + unit */
export const catalogSkuSpecs = pgTable(
  "catalog_sku_specs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    catalogSkuId: uuid("catalog_sku_id")
      .notNull()
      .references(() => catalogSkus.id, { onDelete: "cascade" }),
    specKey: varchar("spec_key", { length: 100 }).notNull(),
    specValue: varchar("spec_value", { length: 500 }).notNull(),
    specUnit: varchar("spec_unit", { length: 50 }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull(),
  },
  (t) => [
    index("catalog_sku_specs_sku").on(t.catalogSkuId),
    index("catalog_sku_specs_sku_key").on(t.catalogSkuId, t.specKey),
  ],
);

/** Fitment ของ catalog SKU — รถ configuration ที่ใช้ได้ */
export const catalogFitments = pgTable(
  "catalog_fitments",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    catalogSkuId: uuid("catalog_sku_id")
      .notNull()
      .references(() => catalogSkus.id, { onDelete: "cascade" }),
    vehicleConfigurationId: uuid("vehicle_configuration_id")
      .notNull()
      .references(() => vehicleConfigurations.id),
    fitmentNote: text("fitment_note"),
    quantityPerVehicle: integer("quantity_per_vehicle"),
    applicabilityCode: varchar("applicability_code", { length: 100 }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull(),
  },
  (t) => [
    index("catalog_fitments_sku_configuration").on(
      t.catalogSkuId,
      t.vehicleConfigurationId,
    ),
  ],
);

/** โครงเมนู EPC โดย vehicle configuration */
export const epcGroups = pgTable(
  "epc_groups",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    vehicleConfigurationId: uuid("vehicle_configuration_id")
      .notNull()
      .references(() => vehicleConfigurations.id),
    parentGroupId: uuid("parent_group_id").references(
      (): AnyPgColumn => epcGroups.id,
    ),
    groupCode: varchar("group_code", { length: 50 }),
    groupName: varchar("group_name", { length: 255 }).notNull(),
    sortOrder: integer("sort_order").default(0).notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull(),
  },
  (t) => [
    index("epc_groups_configuration").on(t.vehicleConfigurationId),
    index("epc_groups_parent").on(t.parentGroupId),
  ],
);

/** ภาพบีบแตกภายในกลุ่ม — image_url จาก storage/CDN */
export const epcDiagrams = pgTable(
  "epc_diagrams",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    epcGroupId: uuid("epc_group_id")
      .notNull()
      .references(() => epcGroups.id, { onDelete: "cascade" }),
    title: varchar("title", { length: 255 }),
    imageUrl: text("image_url").notNull(),
    sortOrder: integer("sort_order").default(0).notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull(),
  },
  (t) => [index("epc_diagrams_group").on(t.epcGroupId)],
);

/** Hotspot interactive ต่อสินค้า catalog บนภาพ (% จากมุมซ้ายบน) */
export const epcDiagramParts = pgTable(
  "epc_diagram_parts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    epcDiagramId: uuid("epc_diagram_id")
      .notNull()
      .references(() => epcDiagrams.id, { onDelete: "cascade" }),
    catalogProductId: uuid("catalog_product_id")
      .notNull()
      .references(() => catalogProducts.id, { onDelete: "cascade" }),
    calloutNumber: varchar("callout_number", { length: 10 }).notNull(),
    /** ຈຸດເຄື່ອງຫາຍ ຫຼື ກາງ ຂອງກອ່ອງ ຕາມເປົໍ້າເປັນ %) */
    xPct: numeric("x_pct", { precision: 5, scale: 2 }).notNull(),
    yPct: numeric("y_pct", { precision: 5, scale: 2 }).notNull(),
    /** ຄວາມກວ້າງ/ສູງ ຂອງກອ່ອງອ້າງອີງກັບກູນ (ເປັນ % ຂອງແຜນ). null = ສະແດງເປັນຈຸດດຽວ */
    widthPct: numeric("width_pct", { precision: 5, scale: 2 }),
    heightPct: numeric("height_pct", { precision: 5, scale: 2 }),
    quantityPerVehicle: integer("quantity_per_vehicle"),
    applicabilityNote: text("applicability_note"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull(),
  },
  (t) => [
    index("epc_diagram_parts_diagram").on(t.epcDiagramId),
    index("epc_diagram_parts_product").on(t.catalogProductId),
  ],
);

/** SKU ที่เลือกสำหรับ callout (หลาย SKU ต่อ callout) */
export const epcDiagramPartSkus = pgTable(
  "epc_diagram_part_skus",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    epcDiagramPartId: uuid("epc_diagram_part_id")
      .notNull()
      .references(() => epcDiagramParts.id, { onDelete: "cascade" }),
    catalogSkuId: uuid("catalog_sku_id")
      .notNull()
      .references(() => catalogSkus.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull(),
  },
  (t) => [
    index("epc_diagram_part_skus_part").on(t.epcDiagramPartId),
    index("epc_diagram_part_skus_sku").on(t.catalogSkuId),
  ],
);

/** ร้านนี้มีสิทธิ์ขาย catalog product ไหน */
export const shopProducts = pgTable(
  "shop_products",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    shopId: uuid("shop_id")
      .notNull()
      .references(() => shops.id, { onDelete: "cascade" }),
    catalogProductId: uuid("catalog_product_id")
      .notNull()
      .references(() => catalogProducts.id, { onDelete: "cascade" }),
    status: varchar("status", { length: 50 })
      .$type<"active" | "hidden">()
      .default("active")
      .notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull(),
  },
  (t) => [
    index("shop_products_shop").on(t.shopId),
    index("shop_products_catalog").on(t.catalogProductId),
    index("shop_products_shop_catalog").on(t.shopId, t.catalogProductId),
  ],
);

/** ต่อร้าน ต่อ catalog_sku: ราคา / SKU / barcode เป็นของร้านเอง */
export const shopProductSkus = pgTable(
  "shop_product_skus",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    shopId: uuid("shop_id")
      .notNull()
      .references(() => shops.id, { onDelete: "cascade" }),
    catalogSkuId: uuid("catalog_sku_id")
      .notNull()
      .references(() => catalogSkus.id, { onDelete: "cascade" }),
    sku: varchar("sku", { length: 100 }).notNull(),
    barcode: varchar("barcode", { length: 100 }),
    price: numeric("price", {
      precision: 14,
      scale: 2,
      mode: "number",
    }).notNull(),
    cost: numeric("cost", { precision: 14, scale: 2, mode: "number" }),
    currency: varchar("currency", { length: 10 })
      .$type<ShopCurrency>()
      .default("LAK")
      .notNull(),
    isActive: boolean("is_active").notNull().default(true),
    /** none = ສະຕ໋ອກລວມ; lot = ຕາມ lot; lot_serial = serial ຕໍ່ຊິ້ນຜູ້ lot (ບໍ່ມີ serial ລອຍ) */
    trackingMode: varchar("tracking_mode", { length: 20 })
      .$type<"none" | "lot" | "lot_serial">()
      .default("none")
      .notNull(),
    /** ຄ່າເລີ່ມຕົ້ນຕໍ່ SKU — 0 = ບໍ່ມີປະກັນ; null = ຍັງບໍ່ຕັ້ງ */
    warrantyDurationMonths: integer("warranty_duration_months"),
    warrantyMaxOdometerKm: integer("warranty_max_odometer_km"),
    warrantyTermsNote: text("warranty_terms_note"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull(),
  },
  (t) => [
    index("shop_product_skus_shop").on(t.shopId),
    index("shop_product_skus_catalog").on(t.catalogSkuId),
    index("shop_product_skus_shop_catalog").on(t.shopId, t.catalogSkuId),
    index("shop_product_skus_sku").on(t.shopId, t.sku),
    index("shop_product_skus_barcode").on(t.shopId, t.barcode),
  ],
);

export const mediaAssets = pgTable(
  "media_assets",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    ownerType: varchar("owner_type", { length: 50 })
      .$type<
        | "catalog_product"
        | "catalog_sku"
        | "listing"
        | "job"
        | "user"
        | "mechanic"
        | "brand"
        | "vehicle_make"
        | "epc_diagram"
        | "wallet"
      >()
      .notNull(),
    ownerId: uuid("owner_id").notNull(),
    /** ชนิดของ media เช่น id_card (รูปบัตรช่าง) */
    tag: varchar("tag", { length: 50 }),
    /** ลำดับการแสดง (ใช้กับ catalog_sku image — อันแรก = thumbnail) */
    sortOrder: integer("sort_order"),
    url: text("url").notNull(),
    mime: varchar("mime", { length: 100 }),
    width: integer("width"),
    height: integer("height"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
  },
  (t) => [
    index("media_assets_owner").on(t.ownerType, t.ownerId),
    index("media_assets_owner_tag").on(t.ownerType, t.ownerId, t.tag),
  ],
);
